"use client";

import { Grid, Col, Button, ProgressBar, TextInput } from "@tremor/react";
// import { ReCaptcha } from "next-recaptcha-v3";
import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { PaperAirplaneIcon } from "@heroicons/react/24/solid";

import getLanguage from "@/app/utils/getlanguage";
import cleanUpText from "@/app/utils/cleanup.text";
import translation from "./locale/index.json";
import Typewriter from "@/app/components/Typewriter";
import { IConversationTask } from "@/app/interfaces/IConversationTask";
import convertMarkdownToHTML from "@/app/utils/markdown";
import { useLocale } from "@/app/context/use-locale";

export interface IServiceChat {
  userAgent?: string;
  referer?: string;
  language?: string;
  greeting: string;
  minHeight: string;
  maxHeight: string;
  taskId?: string | undefined;
  compact?: boolean;
}

const header = {
  "Content-Type": "application/json",
};

export default function ServiceChat(props: IServiceChat) {
  const { greeting, minHeight, userAgent, referer, language, taskId, compact } = props;
  const conversationId = `talk_${taskId}`;
  const locale = useLocale();
  const content = getLanguage(translation, locale);
  const [progress, setProgress] = useState(false);
  const [progressValue, setProgressValue] = useState(35);
  const [ask, setAsk] = useState("");
  const [typeString, setTypeString] = useState("");
  const [clear] = useState(false);
  const [conversation, setConversation] = useState<IConversationTask[]>([]);
  const [token, setToken] = useState<string | null>(null);
  const [score, setScore] = useState(0);
  const [markdown, setMarkdown] = useState("");

  // client-side fallback when props are not provided (e.g. from ChatWidget)
  const browserAgent = userAgent || (typeof navigator !== "undefined" ? navigator.userAgent : "");
  const browserLang = language || (typeof navigator !== "undefined" ? navigator.language : "");

  useEffect(() => {
    convertMarkdownToHTML(typeString).then(txt => setMarkdown(txt));
  }, [typeString, markdown, setMarkdown, score, locale]);

  useEffect(() => {
    if (token) {
      fetch("/api/auth/recaptcha", {
        headers: header,
        method: "POST",
        body: JSON.stringify({ token }),
      })
        .then(async response => {
          const responseRecaptcha = await response.json();
          if (responseRecaptcha.status === 200) {
            setScore(responseRecaptcha.score);
          }
        })
        .catch(err => {
          console.warn(`chat-bot recaptcha ${err?.message}`, err);
        });
    }
  }, [token]);

  const start = useCallback(async () => {
    // if (score < 0.5) {
    //   toast.error(`${content.dismiss} ${score}`);
    //   return;
    // }

    try {
      setProgress(true);
      setProgressValue(15);
      setTimeout(() => setProgressValue(55), 1500);
      setTimeout(() => setProgressValue(75), 2200);
      setTimeout(() => setProgressValue(95), 3200);
      const context = {
        taskId: taskId || undefined,
        ask: cleanUpText(`${ask ? ask : content.prompt}`),
        context: cleanUpText(
          `<user_browser>${browserAgent}</user_browser>
          <user_referer_site>${referer || ""}</user_referer_site>
          <user_language>${locale || browserLang}</user_language>
          <user_page_url>${typeof window !== "undefined" ? window.location.href : ""}</user_page_url>
          <instruction>You must respond to the user in the following language: ${locale || browserLang}. Write all your answers in this language.</instruction>`,
        ),
        conversation,
      };

      const response = await fetch("/api/chat", {
        method: "POST",
        headers: header,
        body: JSON.stringify(context),
      });

      const json = await response.json();
      const { status, data, error } = json;

      if (status >= 200 && status < 202) {
        setAsk("");
        let text = [];

        if (data.match(/FINISH_ACTION/gu)) {
          const _space = `\n ${content.finishText}`;
          text = _space?.replace(/\d+\. /g, "\n").split("\n") || [];
          conversation.push({
            user: context.ask,
            assistant: content.finishText,
          });
          setConversation(conversation);
        } else {
          const _space = `\n ${data}`;
          text = _space?.replace(/\d+\. /g, "\n").split("\n") || [];
          conversation.push({
            user: context.ask,
            assistant: data,
          });
          setConversation(conversation);
        }
        if (window) window.localStorage.setItem(conversationId, JSON.stringify(conversation.slice(-20)));
        setTypeString(text?.join("<br>") || " ");
      } else {
        toast.error(error);
      }
    } catch (error) {
      setProgress(false);
      if (error) console.error("Error saving chat", error);
    } finally {
      setProgressValue(0);
      setProgress(false);
    }
  }, [ask, content.finishText, conversation, locale, referer, taskId, browserAgent, browserLang, conversationId]);

  // Restore conversation history and last message on mount / page navigation
  useEffect(() => {
    const saved = window.localStorage.getItem(conversationId);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (parsed.length) {
        setConversation(parsed);
        const lastAssistant = parsed[parsed.length - 1]?.assistant;
        if (lastAssistant) {
          setTypeString(lastAssistant);
          return;
        }
      }
    }
    // No saved history – show greeting if provided
    if (greeting) {
      setTimeout(() => setTypeString(greeting), 300);
    }
  }, [conversationId, greeting]);

  return (
    <>
      {/* <ReCaptcha onValidate={setToken} action="submit" /> */}
      <div className="w-full">
        <Typewriter
          className={`bg-white rounded-lg ${compact ? "p-2 text-sm" : "p-4"}`}
          text={markdown}
          clear={clear}
          maxHeight={minHeight || "30vh"}
        />

        <Grid numItems={1} numItemsSm={12} className={`w-full gap-2 ${compact ? "pt-4 pb-2" : "pt-10 pb-10"}`}>
          {progress ? (
            <Col numColSpan={1} numColSpanSm={12}>
              <ProgressBar value={progressValue} />
            </Col>
          ) : (
            ""
          )}

          <Col numColSpanSm={compact ? 9 : 10} numColSpan={1} className="w-full">
            <TextInput
              onKeyDown={e => (e.keyCode === 13 ? start() : null)}
              autoFocus={!compact}
              value={ask}
              onChange={e => setAsk(e.target.value)}
              placeholder={compact ? content.btn : undefined}
            />
          </Col>

          <Col numColSpan={1} numColSpanSm={compact ? 2 : 2} className="w-full my-auto mx-auto">
            <Button
              icon={PaperAirplaneIcon}
              className="w-full brand-gradient cta-glow border-transparent text-white font-semibold shadow-lg transition hover:opacity-90"
              onClick={start}
            />
          </Col>
        </Grid>
      </div>
    </>
  );
}
